import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { BrandLogo, Button, Card, FormField, Input } from '@/ui';
import { postLogin } from '@/api/iam';
import { useSessionStore } from '@/auth/session.store';
import { safeNextPath } from '@/auth/next-path';
import { zoneHome } from '@/auth/zone-home';
import { authErrorMessage } from './error-message';
import styles from './AuthScreens.module.css';

/**
 * Giris ekrani.
 *
 * KENDI KENDINE KAYIT YOK ve SIFRE SIFIRLAMA UCU YOK — ikisi de urun karari:
 * hesaplar ya buro yoneticisi ya platform yoneticisi tarafindan aciliyor
 * (POST /v1/organizations/me/members/create), gecici sifre e-postayla gidiyor
 * ve ilk giriste degistiriliyor. Ekranda bunu SOYLEYEN bir satir olmak zorunda;
 * aksi halde sifresini unutan kullanici hicbir cikis yolu goremez ve oran
 * sinirini (e-posta basina 15 dk'da 5 deneme) tuketene kadar dener.
 */

/**
 * Sema bir FABRIKA: t()'yi disaridan alir, bilesende useMemo ile kurulur.
 * Modul seviyesinde kurulsaydi mesajlar import aninda donar ve dil degisiminde
 * bayat kalirdi.
 */
const makeLoginSchema = (t: (key: string) => string) =>
  z.object({
    userEmail: z
      .string()
      .trim()
      .min(1, { message: t('validation.emailRequired') })
      .pipe(z.email({ message: t('validation.emailInvalid') })),
    userPassword: z.string().min(1, { message: t('validation.passwordRequired') }),
  });

type LoginFormValues = z.input<ReturnType<typeof makeLoginSchema>>;

export function LoginScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const status = useSessionStore((state) => state.status);
  const snapshot = useSessionStore((state) => state.snapshot);
  const adoptAuthPayload = useSessionStore((state) => state.adoptAuthPayload);
  const [serverError, setServerError] = useState<string | null>(null);

  const schema = useMemo(() => makeLoginSchema(t), [t]);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { userEmail: '', userPassword: '' },
  });

  // Zaten oturumu acik biri /giris'e gelirse kendi bolgesine birak.
  if (status === 'authenticated' && snapshot) {
    return <Navigate to={zoneHome(snapshot)} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      const payload = await postLogin({
        userEmail: values.userEmail.trim(),
        userPassword: values.userPassword,
      });
      if (payload.code !== 'success') {
        setServerError(payload.message ?? t('auth.invalidCredentials'));
        return;
      }
      const session = adoptAuthPayload(payload);
      if (!session) {
        setServerError(t('auth.loginErrorDetail'));
        return;
      }

      // KAPILAR ONCE. Zorunlu sifre degisimi ve sozlesme onayi bolge
      // yonlendirmesinin ONUNDE; `next` ancak ikisi de gecildiyse onurlandirilir.
      if (session.mustChangePassword) {
        void navigate('/ilk-giris', { replace: true });
        return;
      }
      if (!session.consents.kvkkAccepted || !session.consents.usageAgreementAccepted) {
        void navigate('/sozlesmeler', { replace: true });
        return;
      }
      // `next` adres cubugundan geliyor — dogrulanmadan kullanilirsa acik
      // yonlendirme olur (bkz. src/auth/next-path.ts).
      const next = safeNextPath(searchParams.get('next'));
      void navigate(next ?? zoneHome(session), { replace: true });
    } catch (error) {
      setServerError(authErrorMessage(error, t));
    }
  });

  return (
    <Card padding="lg" className={styles.card}>
      {/* Grafik marka — ekran okuyucuya h1 yeterli. */}
      <BrandLogo variant="full" className={styles.logo} />
      <h1 className={styles.title}>{t('auth.signIn')}</h1>
      <p className={styles.subtitle}>{t('auth.tagline')}</p>

      <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
        <FormField label={t('profile.emailAddress')} error={errors.userEmail} required>
          <Input
            type="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            {...register('userEmail')}
          />
        </FormField>

        <FormField label={t('auth.password')} error={errors.userPassword} required>
          <Input type="password" autoComplete="current-password" {...register('userPassword')} />
        </FormField>

        {serverError ? (
          <p className={styles.error} role="alert">
            {serverError}
          </p>
        ) : null}

        {/* Istek surerken kilitli: her tiklama yeni bir istek demek ve sunucudaki
            oran siniri (e-posta basina 15 dk'da 5 deneme) tukenebilir. */}
        <Button type="submit" size="lg" block loading={isSubmitting}>
          {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
      </form>

      <p className={styles.note}>{t('auth.noSelfSignup')}</p>
    </Card>
  );
}
